import React from 'react'
import { X } from 'lucide-react'
import { useBackgroundTasks } from '@/contexts/BackgroundTaskContext'

interface EmbeddingProgressModalProps {
  isOpen: boolean
  onClose: () => void
}

const getTaskTypeLabel = (taskName: string): string => {
  switch (taskName) {
    case 'embedding_upload':
      return 'Uploading Images'
    case 'embedding_index_rebuild':
      return 'Rebuilding Index'
    case 'embedding_initialize':
      return 'Initializing Embeddings'
    case 'embedding_warmup':
      return 'Loading Models'
    default:
      return 'Processing'
  }
}

export const EmbeddingProgressModal: React.FC<EmbeddingProgressModalProps> = ({ isOpen, onClose }) => {
  const { activeEmbeddingTasks } = useBackgroundTasks()

  if (!isOpen || activeEmbeddingTasks.length === 0) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden" style={{ backgroundColor: '#ffffff' }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {activeEmbeddingTasks.length === 1
              ? 'Processing Task'
              : `${activeEmbeddingTasks.length} Active Tasks`}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {activeEmbeddingTasks.map((task) => {
            const progressPercentage = Math.round(task.progress * 100)

            return (
              <div key={task.task_id} className="space-y-2">
                {/* Task Title */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {getTaskTypeLabel(task.name)}
                  </span>
                  <span className="text-sm font-medium text-blue-600">
                    {progressPercentage}%
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>

                {/* Progress Message */}
                {task.progress_message && (
                  <p className="text-sm text-gray-600">
                    {task.progress_message}
                  </p>
                )}

                {/* Task Status Badge */}
                <div className="flex items-center gap-2">
                  {task.status === 'running' && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Running
                    </span>
                  )}
                  {task.status === 'pending' && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Pending
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            Processing in background. You can close this and continue working.
          </p>
        </div>
      </div>
    </div>
  )
}
